import { alpha, createTheme as muiCreateTheme } from "@mui/material/styles"
import {
  blue, green, grey, orange, red,
} from "@mui/material/colors"
import { backdropClasses } from "@mui/material"
import constants from "Services/constants"

const createGlobalStyle = (mode, product = constants.ORGANIZATION_PRODUCT_CLOUDERP) => {
  const darkMode = mode === "dark"
  const isProfitDrive = product === constants.ORGANIZATION_PRODUCT_PROFITDRIVE
  const base = {
    body: {
      background: darkMode ? "#121212" : "#f7f7f7",
    },
    "*::-webkit-scrollbar-track": {
      WebkitBoxShadow: "inset 0 0 6px rgba(0,0,0,0.3)",
      backgroundColor: darkMode ? "#121212" : "#f5f5f5",
    },
    "*::-webkit-scrollbar": {
      width: "7px",
      height: "7px",
      backgroundColor: darkMode
        ? (isProfitDrive ? "#14532d" : "#0b5b79")
        : "#f5f5f5",
      borderRadius: "4px",
    },
    "*::-webkit-scrollbar-thumb": {
      WebkitBoxShadow: "inset 0 0 6px rgba(0,0,0,.3)",
      backgroundColor: darkMode
        ? (isProfitDrive ? "#16a34a" : "#0e2844")
        : (isProfitDrive ? "#16a34a" : "#3885bb"),
    },
  }
  return base
}

const createTheme = (mode, product = constants.ORGANIZATION_PRODUCT_CLOUDERP) => {
  const darkMode = mode === "dark"
  const isProfitDrive = product === constants.ORGANIZATION_PRODUCT_PROFITDRIVE
  const palette = {
    mode,
    primary: {
      dark: isProfitDrive
        ? (darkMode ? "#15803d" : "#14532d")
        : (darkMode ? "#0767ab" : "#0e2844"),
      pastel: isProfitDrive ? "#166534" : "#0b5b79",
      main: isProfitDrive
        ? (darkMode ? "#22c55e" : "#16a34a")
        : (darkMode ? "#3885bb" : "#0767ab"),
      light: isProfitDrive ? "#22c55e" : "#3885bb",
      menuIcon: isProfitDrive ? "#86efac" : "#92bcdb",
      menuItemSelected: isProfitDrive ? "#14532d" : "#2e405d",
      contrastText: "rgb(255, 255, 255)",
    },
    secondary: {
      dark: isProfitDrive
        ? (darkMode ? "#15803d" : "#166534")
        : (darkMode ? "#0767ab" : "#0e2844"),
      pastel: isProfitDrive ? "#166534" : "#0b5b79",
      main: isProfitDrive
        ? (darkMode ? "#22c55e" : "#16a34a")
        : (darkMode ? "#3885bb" : "#0767ab"),
      light: isProfitDrive ? "#22c55e" : "#3885bb",
      contrastText: "rgb(255, 255, 255)",
    },
    info: {
      dark: blue[600],
      pastel: blue[300],
      main: "#2162ce",
      light: blue[100],
      contrastText: "rgb(255, 255, 255)",
    },
    success: {
      dark: green[600],
      pastel: green[300],
      main: "#37833b",
      light: green[100],
      contrastText: "rgb(255, 255, 255)",
    },
    warning: {
      dark: orange[600],
      pastel: orange[300],
      main: "#c66800",
      light: orange[100],
      contrastText: "rgb(255, 255, 255)",
    },
    error: {
      dark: "#a52c55",
      pastel: red[300],
      main: "#c23564",
      light: red[100],
      contrastText: "rgb(255, 255, 255)",
    },
    neutral: {
      dark: grey[600],
      pastel: grey[300],
      main: "#474956",
      light: grey[100],
      contrastText: "rgb(255, 255, 255)",
    },
    menu: {
      background: isProfitDrive
        ? "#000000"
        : (darkMode ? "#0f1720" : "#0e2844"),
      icon: isProfitDrive ? "#86efac" : "#92bcdb",
      text: "#ffffff",
      itemSelected: isProfitDrive
        ? "#14532d"
        : (darkMode ? alpha("#3885bb", 0.2) : "#2e405d"),
      divider: isProfitDrive
        ? alpha("#86efac", 0.55)
        : (darkMode ? "#3f3f3f" : "#92bcdb"),
      submenuBackground: isProfitDrive
        ? alpha("#000000", 0.98)
        : (darkMode ? alpha("#3885bb", 0.2) : alpha("#0e2844", 0.99)),
    },
    background: {
      default: darkMode ? "#121212" : "#f7f7f7",
      paper: darkMode ? "#1a1a1a" : "#ffffff",
    },
    text: {
      primary: darkMode ? "#e3e7ee" : "#111827",
      secondary: darkMode ? "#b7c0ce" : grey[700],
      disabled: darkMode ? "#7f8a9a" : grey[500],
    },
    surface: {
      canvas: darkMode ? "#121212" : "#f7f7f7",
      page: darkMode ? "#1a1a1a" : "#f2f3f7",
      raised: darkMode ? "#202020" : "#f6f7fb",
      overlay: darkMode ? "#252525" : "#f2f3f7",
      active: darkMode ? "#253752" : "#d4edf933",
      columnActions: darkMode ? "rgb(60 60 60)" : "#ffffff",
      columnActionsShadow: darkMode ? "#3c3c3c" : "#ffffff",
    },
    border: {
      subtle: darkMode ? "#2d2d2d" : "#e1dddd",
      strong: darkMode ? "#3f3f3f" : "#c9c9c9",
      focus: darkMode ? "#6ba6d5" : "#0767ab",
    },
    status: {
      info: {
        bg: darkMode ? "#24467d" : "#dce9ff",
        fg: darkMode ? "#dbe8ff" : "#143469",
        border: darkMode ? "#3a609f" : "#b7cff9",
      },
      success: {
        bg: darkMode ? "#244b2a" : "#dff5e2",
        fg: darkMode ? "#d7f4db" : "#1f5e2e",
        border: darkMode ? "#2f6a3b" : "#b7dfbf",
      },
      warning: {
        bg: darkMode ? "#553f1f" : "#fff1db",
        fg: darkMode ? "#ffe6b9" : "#7a4b0f",
        border: darkMode ? "#7e6238" : "#f0d3a3",
      },
      error: {
        bg: darkMode ? "#5a2436" : "#ffe2ea",
        fg: darkMode ? "#ffdbe6" : "#832646",
        border: darkMode ? "#8a3e58" : "#f5b7cb",
      },
      neutral: {
        bg: darkMode ? "#2b2b2b" : "#eceff4",
        fg: darkMode ? "#dfdfdf" : "#363c47",
        border: darkMode ? "#3f3f3f" : "#d7dde7",
      },
    },
  }

  const tableTheme = {
    surface: darkMode ? palette.surface.page : "#ffffff",
    text: darkMode ? palette.text.primary : "#111827",
    border: darkMode ? palette.border.subtle : "#e5e7eb",
    paginationTopBorder: darkMode ? "#1a1a1a" : "#ffffff",
    selectIcon: darkMode ? palette.text.secondary : "#4b5563",
  }

  const theme = muiCreateTheme({
    typography: {
      useNextVariants: true,
    },
    palette,
    components: {
      MUIDataTable: {
        styleOverrides: {
          caption: {
            display: "none",
          },
          responsiveBase: {
            height: "fit-content!important",
            width: "100%",
            overflow: "auto",
          },
          paper: {
            "@media (max-width:600px)": {
              boxShadow: "none",
            },
          },
        },
      },
      MUIDataTableToolbar: {
        styleOverrides: {
          root: {
            display: "flex !important",
            position: "sticky",
            top: 0,
            zIndex: 9,
            background: tableTheme.surface,
            color: tableTheme.text,
            height: "64px",
            "@media (max-width:600px)": {
              flexDirection: "column-reverse",
              height: "unset",
            },
          },
          filterPaper: {
            width: 500,
          },
          actions: {
            flex: "0 !important",
            minWidth: "fit-content",
          },
          // NOTE: needed because of table loader bug when filter opened
          left: {
            color: palette.primary.main,
            "& > div": {
              display: "none",
            },
            "& > h1": {
              fontSize: "22px",
            },
          },
        },
      },
      MUIDataTableToolbarSelect: {
        styleOverrides: {
          root: {
            "&>div:first-child": {
              display: "none",
            },
            position: "sticky",
            top: 0,
            height: "64px",
            background: tableTheme.surface,
            color: tableTheme.text,
            borderBottom: `1px solid ${tableTheme.border}`,
            "& .MUIDataTableSelectCell-root": {
              background: `${tableTheme.surface} !important`,
              borderBottomColor: `${tableTheme.border} !important`,
            },
            "& .MuiCheckbox-root": {
              color: `${tableTheme.text} !important`,
            },
            "@media (max-width:600px)": {
              height: "unset",
            },
          },
        },
      },
      MUIDataTableFooter: {
        styleOverrides: {
          root: {
            background: tableTheme.surface,
            color: tableTheme.text,
            borderTop: `1px solid ${tableTheme.border}`,
          },
        },
      },
      MUIDataTableSelectCell: {
        styleOverrides: {
          headerCell: {
            background: tableTheme.surface,
          },
          fixedHeader: {
            background: tableTheme.surface,
          },
          root: {
            background: "inherit",
          },
          expandDisabled: {
            visibility: "hidden",
          },
        },
      },
      MUIDataTableSearch: {
        styleOverrides: {
          main: {
            display: "flex !important",
            maxWidth: 400,
            "@media (max-width:600px)": {
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            },
          },
          searchIcon: {
            "@media (max-width:960px)": {
              color: palette.primary.contrastText,
              width: 0,
              marginRight: 0,
            },
            "@media (max-width:600px)": {
              marginLeft: 35,
            },
          },
          searchText: {
            "@media (max-width:600px)": {
              width: 224,
            },
          },
        },
      },
      MUIDataTableBodyRow: {
        styleOverrides: {
          root: {
            "&:nth-child(odd)": {
              backgroundColor: darkMode ? alpha(palette.surface.page, 0.7) : grey[100],
            },
          },
        },
      },
      MUIDataTableBodyCell: {
        styleOverrides: {
          root: {
            padding: 8,
            minWidth: "unset",
            whiteSpace: "nowrap",
            "@media (max-width:600px)": {
              minWidth: "auto",
            },
            "& p": {
              whiteSpace: "nowrap",
            },
            "@media (max-width:960px)": {
              minWidth: "auto",
              padding: "4px !important",
            },
            "@media (min-width:960px)": {
              margin: -8,
            },
          },
          footer: {
            float: "left",
            padding: "0 !important",
            marginLeft: -8,
          },
          head: {
            position: "sticky",
            top: 0,
            background: tableTheme.surface,
            zIndex: 1,
          },
        },
      },
      MUIDataTableHeadCell: {
        styleOverrides: {
          root: {
            "@media (max-width:600px)": {
              minWidth: "auto",
            },
            "& span": {
              whiteSpace: "nowrap",
            },
          },
        },
      },
      MUIDataTableFilterList: {
        styleOverrides: {
          root: {
            display: "none",
          },
        },
      },
      MUIDataTableRow: {
        styleOverrides: {
          root: {
            "&:nth-child(odd)": {
              backgroundColor: darkMode ? alpha(palette.surface.page, 0.5) : "#e8e8e8",
            },
          },
        },
      },
      MUIDataTablePagination: {
        styleOverrides: {
          actions: {
            "@media (max-width:600px)": {
              marginLeft: "0 !important",
            },
          },
          toolbar: {
            background: tableTheme.surface,
            color: tableTheme.text,
            borderTop: `1px solid ${tableTheme.paginationTopBorder}`,
            "& > p:first-of-type": {
              "@media (max-width:600px)": {
                display: "none",
              },
            },
          },
          caption: {
            color: tableTheme.text,
          },
          input: {
            color: tableTheme.text,
          },
          selectIcon: {
            color: tableTheme.selectIcon,
          },
          navContainer: {
            float: "left",
          },
        },
      },
      // MuiModal: {
      //   styleOverrides: {
      //     root: {
      //       [`&:has(> div.${backdropClasses.root}[style*="opacity: 0"])`]: {
      //         pointerEvents: "none",
      //       },
      //     },
      //   },
      // },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            '&[style*="opacity: 0"]': {
              pointerEvents: "none",
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            color: palette.text.primary,
            "&::placeholder": {
              color: palette.text.disabled,
              opacity: 1,
              fontStyle: "italic",
            },
            "&.Mui-disabled": {
              WebkitTextFillColor: palette.text.disabled,
              color: palette.text.disabled,
            },
          },
          root: {
            color: palette.text.primary,
            "&.Mui-disabled": {
              color: palette.text.disabled,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: palette.text.secondary,
            "&.Mui-focused": {
              color: palette.text.primary,
            },
            "&.Mui-disabled": {
              color: palette.text.disabled,
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            color: palette.text.secondary,
            "&.Mui-disabled": {
              color: palette.text.disabled,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            borderColor: palette.border.subtle,
          },
          root: {
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: palette.border.strong,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: palette.border.focus,
            },
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          underline: {
            "&:before": {
              borderBottomColor: palette.border.subtle,
            },
            "&:hover:not(.Mui-disabled):before": {
              borderBottomColor: palette.border.strong,
            },
            "&:after": {
              borderBottomColor: palette.border.focus,
            },
          },
        },
      },
    },
  })

  theme.darkMode = darkMode
  theme.color = ([light, dark]) => (darkMode ? (dark ?? light) : light)
  theme.custom = {
    alpha: (colorValue, opacity) => alpha(colorValue, opacity),
    surface: (level = "page") => theme.palette.surface[level] || theme.palette.surface.page,
    status: (name = "neutral") => theme.palette.status[name] || theme.palette.status.neutral,
  }
  return theme
}

export { createGlobalStyle, createTheme }
